import React, { useCallback, useRef, useContext } from 'react';
import serialize from 'form-serialize';

import { AppContext } from '@prisjakt/ui-app-context';

import Spinner from '@prisjakt/ui-spinner';

import { ShopReviewFormData } from '../../../../src/areas/reviews/types';

import * as S from './styles';

export enum ActionType {
  CREATE = 'create',
  EDIT = 'edit',
  PUBLISH = 'publish',
}

type Props = {
  className?: string;
  children: React.ReactNode;
  id?: string;
  onSubmit: (formData: ShopReviewFormData) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  pending?: boolean;
  actionType: ActionType;
  onChange?: (formData: ShopReviewFormData) => void;
};

const ReviewForm = ({
  className,
  children,
  id,
  onSubmit,
  onCancel,
  onDelete,
  pending,
  actionType,
  onChange,
}: Props) => {
  const { t } = useContext(AppContext);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();

      const formData = serialize(formRef.current, { hash: true });
      onSubmit(formData);
    },
    [onSubmit]
  );

  const handleChange = useCallback(() => {
    if (onChange) {
      const formData = serialize(formRef.current, { hash: true });
      onChange(formData);
    }
  }, [onChange]);

  const submitButtonText = () => {
    switch (actionType) {
      case 'create':
        return t('account', 'forgot-password.submit-button');
      case 'publish':
        return t('reviews', 'dialog.publish');
      default:
        return t('reviews', 'dialog.update');
    }
  };

  return (
    <S.Form
      className={className}
      method="post"
      id={id}
      ref={formRef}
      onSubmit={handleSubmit}
      onChange={handleChange}
    >
      {children}
      <S.Buttons>
        {onDelete && (
          <S.DeleteButton
            color="negative"
            variant="outlined"
            size="small"
            onClick={onDelete}
          >
            {t('reviews', 'dialog.delete')}
          </S.DeleteButton>
        )}

        {onCancel && (
          <S.CancelButton
            variant="text"
            color="primary"
            size="small"
            onClick={onCancel}
          >
            {t('common', 'button.cancel')}
          </S.CancelButton>
        )}

        <S.SubmitButton
          type="submit"
          color="primary"
          size="small"
          disabled={pending}
        >
          {pending ? (
            <Spinner isActive={pending} position="none" />
          ) : (
            submitButtonText()
          )}
        </S.SubmitButton>
      </S.Buttons>
    </S.Form>
  );
};

export default ReviewForm;
